import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Star, Quote } from "lucide-react";

export default function TestimonialsSection() {
  const [hoveredCard, setHoveredCard] = useState(null);

  const testimonials = [
    {
      name: "Sarah Lin",
      title: "Marketing Director, TechCorp",
      image: "https://img.freepik.com/free-photo/front-view-sign-language-concept_23-2148572467.jpg?semt=ais_hybrid&w=740&q=80",
      quote: "SafoLife Technologies transformed our outdated online presence into a sleek, modern platform. We've seen a 45% increase in engagement and 30% more conversions since launch. Their team is truly exceptional!",
      rating: 5,
      project: "Website Redesign"
    },
    {
      name: "Jason Turner",
      title: "CEO, Startup Ventures",
      image: "https://photoheads.co.uk/wp-content/uploads/2020/05/headshot-with-client-testimonial.jpg",
      quote: "They don't just design; they understand your business. The brand strategy and web development provided by SafoLife Technologies were pivotal in our successful market entry and funding round.",
      rating: 5,
      project: "Brand Strategy & Development"
    },
    {
      name: "Mia Reynolds",
      title: "Creative Director, Design Studio",
      image: "https://img.freepik.com/free-photo/close-up-young-person-barbeque_23-2149271990.jpg",
      quote: "Working with SafoLife was smooth from day one. The designs were sharp, the communication was clear, and delivery was always ahead of schedule. A truly professional partnership.",
      rating: 5,
      project: "Mobile App Design"
    },
    {
      name: "Ava Nishi",
      title: "Founder, EcoStart",
      image: "https://media.istockphoto.com/id/1324194583/photo/head-shot-portrait-smiling-attractive-woman-blogger-speaking-at-camera.jpg?s=612x612&w=0&k=20&c=1FJKrE_wqVyZhed7cYTgE2raRwHONesu258tl8N6VIQ=",
      quote: "SafoLife helped us go from zero to launch with confidence. Their expertise saved us weeks of work and their templates gave us a professional edge from day one.",
      rating: 4,
      project: "E-commerce Platform"
    },
    {
      name: "Martin Gomez",
      title: "Brand Consultant",
      image: "https://www.hubspot.com/hubfs/Testimonial-lead-gen-1.webp",
      quote: "It's rare to find a design partner who's both strategic and insanely creative. SafoLife gets both aspects perfectly and delivers exceptional results every time.",
      rating: 5,
      project: "Brand Identity"
    },
    {
      name: "Rachel Foster",
      title: "Operations Lead, Wellnify",
      image: "https://media.istockphoto.com/id/168518740/photo/portrait-of-woman-with-short-hair.jpg?s=612x612&w=0&k=20&c=5vGIftrKdgSMJzHdA0bzEYTQKloWi6BYgcJAPyTnfsI=",
      quote: "Every step was thoughtful and intentional. They asked the right questions and delivered beyond expectations. Our user engagement metrics have never been better.",
      rating: 5,
      project: "Web Application"
    },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    }
  };

  return (
    <section className="relative bg-gradient-to-br from-gray-50 to-blue-50 py-24 px-6 md:px-16 lg:px-24 overflow-hidden">
      {/* Background Elements */}
      <div className="absolute top-0 left-0 w-72 h-72 bg-blue-100 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-blob"></div>
      <div className="absolute top-0 right-0 w-72 h-72 bg-indigo-100 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-blob animation-delay-2000"></div>
      <div className="absolute bottom-0 left-1/2 w-72 h-72 bg-purple-100 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-blob animation-delay-4000"></div>

      {/* Header */}
      <div className="text-center mb-20 relative z-10">
        <div className="inline-flex items-center justify-center mb-4">
          <div className="w-2 h-2 bg-blue-600 rounded-full mr-3"></div>
          <p className="text-blue-600 font-semibold text-lg tracking-wider">
            CLIENT TESTIMONIALS
          </p>
          <div className="w-2 h-2 bg-blue-600 rounded-full ml-3"></div>
        </div>
        
        <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight mb-6">
          What Our Clients
          <span className="block bg-gradient-to-r from-blue-600 to-indigo-700 bg-clip-text text-transparent">
            Are Saying
          </span>
        </h2>
        
        <div className="w-24 h-1 bg-gradient-to-r from-blue-500 to-indigo-600 mx-auto rounded-full mb-8"></div>
        
        <p className="text-gray-600 text-lg md:text-xl max-w-2xl mx-auto">
          Don't just take our word for it. Here's what our clients have to say about their experience working with SafoLife Technologies.
        </p>
      </div>

      {/* Testimonials Grid */}
      <motion.div 
        className="relative max-w-7xl mx-auto z-10"
        variants={containerVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-100px" }}
      >
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {testimonials.map((testimonial, index) => (
            <motion.div
              key={index}
              variants={cardVariants}
              className="group relative"
              onMouseEnter={() => setHoveredCard(index)}
              onMouseLeave={() => setHoveredCard(null)}
            >
              {/* Main Card */}
              <div className="bg-white/80 backdrop-blur-sm rounded-3xl p-8 shadow-xl border border-white/50 hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 h-full flex flex-col">
                
                {/* Quote Icon */}
                <div className="absolute top-6 right-6">
                  <Quote className="w-8 h-8 text-blue-600/20" />
                </div>

                {/* Header */}
                <div className="flex items-start mb-6">
                  <div className="relative">
                    <motion.img
                      src={testimonial.image}
                      alt={testimonial.name}
                      className="w-14 h-14 rounded-2xl object-cover shadow-lg"
                      whileHover={{ scale: 1.1 }}
                      transition={{ duration: 0.3 }}
                    />
                    <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-green-500 rounded-full border-2 border-white"></div>
                  </div>
                  <div className="ml-4 flex-1">
                    <h3 className="text-lg font-bold text-gray-900">
                      {testimonial.name}
                    </h3>
                    <p className="text-gray-600 text-sm mb-1">
                      {testimonial.title}
                    </p>
                    <span className="inline-block bg-blue-100 text-blue-700 text-xs px-3 py-1 rounded-full font-medium">
                      {testimonial.project}
                    </span>
                  </div>
                </div>

                {/* Quote */}
                <p className="text-gray-700 leading-relaxed flex-1 mb-6 text-lg">
                  "{testimonial.quote}"
                </p>

                {/* Rating */}
                <div className="flex items-center justify-between pt-4 border-t border-gray-200/50">
                  <div className="flex space-x-1">
                    {Array.from({ length: 5 }).map((_, idx) => (
                      <motion.div
                        key={idx}
                        whileHover={{ scale: 1.2 }}
                        transition={{ duration: 0.2 }}
                      >
                        <Star
                          size={20}
                          className={`${
                            idx < testimonial.rating 
                              ? "text-yellow-400 fill-yellow-400" 
                              : "text-gray-300"
                          }`}
                        />
                      </motion.div>
                    ))}
                  </div>
                  <div className="text-sm text-gray-500 font-medium">
                    {testimonial.rating}.0/5.0
                  </div>
                </div>
              </div>

              {/* Background Glow Effect */}
              <div className="absolute inset-0 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-3xl opacity-0 group-hover:opacity-10 blur-xl -z-10 transition-all duration-500 transform group-hover:scale-105"></div>
            </motion.div>
          ))}
        </div>
      </motion.div>

      {/* Stats Section */}
      <motion.div 
        className="max-w-4xl mx-auto mt-20 bg-white/80 backdrop-blur-sm rounded-3xl p-8 shadow-xl border border-white/50"
        initial={{ opacity: 0, y: 50 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.4 }}
        viewport={{ once: true }}
      >
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
          <div>
            <div className="text-3xl md:text-4xl font-bold text-blue-600 mb-2">98%</div>
            <div className="text-gray-600 font-medium">Client Satisfaction</div>
          </div>
          <div>
            <div className="text-3xl md:text-4xl font-bold text-indigo-600 mb-2">150+</div>
            <div className="text-gray-600 font-medium">Projects Completed</div>
          </div>
          <div>
            <div className="text-3xl md:text-4xl font-bold text-purple-600 mb-2">4.9/5</div>
            <div className="text-gray-600 font-medium">Average Rating</div>
          </div>
          <div>
            <div className="text-3xl md:text-4xl font-bold text-green-600 mb-2">24/7</div>
            <div className="text-gray-600 font-medium">Support Available</div>
          </div>
        </div>
      </motion.div>

      {/* CTA Section */}
      <motion.div 
        className="text-center mt-16 relative z-10"
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.6 }}
        viewport={{ once: true }}
      >
        <div className="bg-gradient-to-r from-blue-600 to-indigo-700 rounded-3xl p-12 text-white shadow-2xl max-w-4xl mx-auto">
          <h3 className="text-2xl md:text-3xl font-bold mb-4">
            Ready to Join Our Happy Clients?
          </h3>
          <p className="text-blue-100 mb-8 text-lg max-w-2xl mx-auto">
            Let's create something amazing together. Your success story could be next!
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <motion.button
              className="bg-white text-blue-600 px-8 py-4 rounded-xl font-bold shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition-all duration-300"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Start Your Project
            </motion.button>
            <motion.button
              className="border-2 border-white text-white px-8 py-4 rounded-xl font-bold hover:bg-white hover:text-blue-600 transition-all duration-300"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              View Case Studies
            </motion.button>
          </div>
        </div>
      </motion.div>

      <style jsx>{`
        @keyframes blob {
          0% { transform: translate(0px, 0px) scale(1); }
          33% { transform: translate(30px, -50px) scale(1.1); }
          66% { transform: translate(-20px, 20px) scale(0.9); }
          100% { transform: translate(0px, 0px) scale(1); }
        }
        .animate-blob {
          animation: blob 7s infinite;
        }
        .animation-delay-2000 {
          animation-delay: 2s;
        }
        .animation-delay-4000 {
          animation-delay: 4s;
        }
      `}</style>
    </section>
  );
}